import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ChakraProvider } from "@chakra-ui/react";
import { Provider } from "react-redux";
import { store } from "./store";
import { system } from "./theme";
import AuthInitializer from "./components/AuthInitializer";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/DashboardLayout";
import AutoLogoutHandler from "./components/AutoLogoutHandler";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import ProfilePage from "./pages/ProfilePage";
import DataUploadPage from "./pages/DataUploadPage";
import DataSummaryPage from "./pages/DataSummaryPage";
import SalesAnalyticsPage from "./pages/SalesAnalyticsPage";
import NotificationsPage from "./pages/NotificationsPage";
import MasterDrugIndexPage from "./pages/MasterDrugIndexPage";
import ReviewPage from "./pages/ReviewPage";
import LocationsPage from "./pages/LocationsPage";

import "./index.css";
import { RXAIAssistant } from "./pages/aiAssistant";
import { ChatHistory } from "./pages/aiAssistant/chatHistory";
import { Inventory } from "./pages/inventory";
import ProductDetailsPage from "./pages/inventory/productDetails";
import { Pharmacy } from "./pages/pharmacy";
import PharmacyDetail from "./pages/pharmacy/pharmacyDetail";
import SettingsLayout from "./pages/settings";
import UserManagement from "./pages/settings/components/userManagement";
import ChangePassword from "./pages/settings/components/changePassword";
import { LandingPage } from "./pages/home";
import { Toaster } from "./components/ui/toaster";
import { SessionDetail } from "./pages/aiAssistant/sessionDetail";

function App() {
  return (
    <Provider store={store}>
      <ChakraProvider value={system}>
        <Toaster />
        <AuthInitializer>
          <BrowserRouter>
            <AutoLogoutHandler />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Protected routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="data-upload" element={<DataUploadPage />} />
                <Route path="data-summary" element={<DataSummaryPage />} />
                <Route path="ai-assistant">
                  <Route index element={<Navigate to="chat" replace />} />
                  <Route path="chat" element={<RXAIAssistant />} />
                  <Route path="history" element={<ChatHistory />} />
                  <Route
                    path="session/:sessionId"
                    element={<SessionDetail />}
                  />
                </Route>
                <Route path="inventory" element={<Inventory />} />
                <Route path="inventory/:id" element={<ProductDetailsPage />} />
                <Route path="pharmacy" element={<Pharmacy />} />
                <Route path="pharmacy/:id" element={<PharmacyDetail />} />
                <Route
                  path="analytics/sales"
                  element={<SalesAnalyticsPage />}
                />
                <Route
                  path="master-drug-index"
                  element={<MasterDrugIndexPage />}
                />
                <Route path="normalization-review" element={<ReviewPage />} />
                <Route path="locations" element={<LocationsPage />} />

                <Route path="/settings" element={<SettingsLayout />}>
                  <Route
                    index
                    element={<Navigate to="/settings/users" replace />}
                  />
                  <Route
                    path="users"
                    element={
                      <ProtectedRoute requireAdmin>
                        <UserManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="password" element={<ChangePassword />} />
                </Route>
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthInitializer>
      </ChakraProvider>
    </Provider>
  );
}

export default App;
